import React from 'react';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { decode } from 'he';
import {
  ExternalLink,
  Calendar,
  Tag,
  Link as LinkIcon,
  Image as ImageIcon,
  Video,
  FileText,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Topic } from '../../types';
import { gql } from '@apollo/client';
import { resolveDisplayMediaUrl } from '../../utils/mediaUrl';
import { Loader } from '../common/PageLoader';

const GET_TOPIC = gql`
  query GetTopic($id: ID!) {
    topic(id: $id) {
      id
      title
      summary
      mediaType
      mediaUrl
      publishedDate
      destinationUrl
      alreadyPosted
      category
      keywords
      createdAt
      updatedAt
      dataSource {
        id
        name
        type
        url
      }
    }
  }
`;

interface TopicDetailsModalProps {
  topic: Topic | null;
  isOpen: boolean;
  onClose: () => void;
}

export const TopicDetailsModal: React.FC<TopicDetailsModalProps> = ({ topic, isOpen, onClose }) => {
  const { data, loading, error } = useQuery(GET_TOPIC, {
    variables: { id: topic?.id },
    skip: !topic?.id || !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  const topicData = data?.topic || topic;

  if (!topic) return null;

  const processImageUrl = (url: string | undefined) => {
    const resolved = resolveDisplayMediaUrl(url);
    if (resolved && resolved.includes('wp-content/uploads/')) {
      return resolved.split('?')[0] + '?resize=800%2C600';
    }
    return resolved;
  };

  const stripHtml = (html: string): string => {
    if (!html) return '';
    const decoded = decode(html);
    return decoded.replace(/<[^>]*>/g, '').trim();
  };

  const getMediaTypeIcon = (mediaType: string) => {
    switch (mediaType) {
      case 'VIDEO':
        return <Video className="w-5 h-5" />;
      case 'IMAGE':
      case 'PHOTO':
        return <ImageIcon className="w-5 h-5" />;
      case 'BOOKMARK':
        return <LinkIcon className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'PPpp');
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-core-dark text-cmp-ink z-[120]">
        <DialogHeader>
          <DialogTitle>Media Details</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-8">
            <Loader label="Loading topic details" className="text-cmp-ink-2" />
          </div>
        )}

        {error && (
          <div className="p-4 bg-error/20 border border-error rounded text-error">
            Error loading topic details: {error.message}
          </div>
        )}

        {topicData && !loading && !error && (
          <div className="space-y-6">
            {/* Media Preview */}
            {topicData.mediaUrl && (
              <div className="relative w-full rounded-lg overflow-hidden border border-cmp-line-2 bg-form-dark">
                {topicData.mediaType === 'VIDEO' ? (
                  <video
                    src={processImageUrl(topicData.mediaUrl)}
                    controls
                    className="w-full h-auto max-h-96 object-contain"
                  />
                ) : (
                  <img
                    src={processImageUrl(topicData.mediaUrl)}
                    alt={topicData.title || 'Topic media'}
                    className="w-full h-auto max-h-96 object-contain"
                  />
                )}
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1">Title</label>
                <p className="text-cmp-ink">{decode(topicData.title || 'N/A')}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1">Media Type</label>
                <p className="text-cmp-ink flex items-center gap-2">
                  {getMediaTypeIcon(topicData.mediaType || '')}
                  {topicData.mediaType || 'N/A'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1">Status</label>
                <p className="text-cmp-ink">
                  {topicData.alreadyPosted ? (
                    <span className="text-green-400">Already Posted</span>
                  ) : (
                    <span className="text-yellow-400">Not Posted</span>
                  )}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1">Category</label>
                <p className="text-cmp-ink">{topicData.category || 'N/A'}</p>
              </div>
            </div>

            {/* Summary */}
            <div>
              <label className="text-sm font-medium text-cmp-ink-2 block mb-1">Summary</label>
              <p className="text-cmp-ink whitespace-pre-wrap">
                {stripHtml(topicData.summary || 'N/A')}
              </p>
            </div>

            {/* Keywords */}
            {topicData.keywords && (
              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Keywords
                </label>
                <div className="flex flex-wrap gap-2">
                  {typeof topicData.keywords === 'string' ? (
                    <span className="px-2 py-1 bg-primary-dark rounded text-sm text-onfill">
                      {topicData.keywords}
                    </span>
                  ) : Array.isArray(topicData.keywords) ? (
                    topicData.keywords.map((keyword: string, index: number) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-primary-dark rounded text-sm text-onfill"
                      >
                        {keyword}
                      </span>
                    ))
                  ) : null}
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Published Date
                </label>
                <p className="text-cmp-ink">{formatDate(topicData.publishedDate)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1">Created At</label>
                <p className="text-cmp-ink">{formatDate(topicData.createdAt)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1">Updated At</label>
                <p className="text-cmp-ink">{formatDate(topicData.updatedAt)}</p>
              </div>
            </div>

            {/* Destination URL */}
            {topicData.destinationUrl && (
              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Destination URL
                </label>
                <a
                  href={topicData.destinationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-light hover:underline break-all"
                >
                  {topicData.destinationUrl}
                </a>
              </div>
            )}

            {/* Media URL */}
            {topicData.mediaUrl && (
              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1">Media URL</label>
                <a
                  href={topicData.mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-light hover:underline break-all"
                >
                  {topicData.mediaUrl}
                </a>
              </div>
            )}

            {/* Data Source */}
            {topicData.dataSource && (
              <div>
                <label className="text-sm font-medium text-cmp-ink-2 block mb-1">Data Source</label>
                <div className="bg-form-dark p-3 rounded border border-cmp-line-2">
                  <p className="text-cmp-ink font-medium">{topicData.dataSource.name}</p>
                  <p className="text-cmp-ink-2 text-sm">Type: {topicData.dataSource.type}</p>
                  {topicData.dataSource.url && (
                    <a
                      href={topicData.dataSource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-light hover:underline text-sm break-all"
                    >
                      {topicData.dataSource.url}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
